import { isFeCode } from "../../../lib/http/errors";
import type { FeCode } from "../../../lib/http/errors";
import { boardKind } from "../../../lib/api/fieldApi";
import type { BenchSource, CampusSource, ValidationIssue } from "../../../lib/api/fieldApi";
import type { StageView } from "../types";
import { staticReason } from "../../../lib/staticReason";
import type { StaticReason } from "../../../lib/staticReason";

/**
 * 검증 V-01~06 → 학생 어휘 단계(모양·부품·연결·기어·포트·프로그램) — SCR v0.6 FS-02 와이어 · SR-CMP-01.
 * 첫 실패에서 멈춘다(그 뒤 단계는 '안 봄'). V-07(구동 바퀴 판정)은 교사 경고라 학생 단계에 없다.
 */
export const STAGES = ["shape", "parts", "link", "gear", "port", "program"] as const;
export type StageId = (typeof STAGES)[number];

const BY_RULE: Record<string, StageId> = {
  "V-01": "shape",
  "V-02": "parts",
  "V-03": "link",
  "V-04": "gear",
  "V-05": "port",
  "V-06": "program",
};
const BY_CODE: Partial<Record<FeCode, StageId>> = {
  "FE-101": "shape",
  "FE-102": "parts",
  "FE-103": "gear",
  "FE-104": "port",
  "FE-201": "program",
};
const CODE_OF_STAGE: Record<StageId, FeCode> = {
  shape: "FE-101",
  parts: "FE-102",
  link: "FE-101",
  gear: "FE-103",
  port: "FE-104",
  program: "FE-201",
};

/** 학생에게 보일 수 있는 FS-02 코드(SRS 부록 B) */
const FS02_CODES: readonly FeCode[] = ["FE-101", "FE-102", "FE-103", "FE-104", "FE-201"];

export const isFs02Code = (c: string): c is FeCode => (FS02_CODES as readonly string[]).includes(c);

export function stageOf(issue: Pick<ValidationIssue, "rule" | "code">): StageId | null {
  const r = BY_RULE[issue.rule.toUpperCase()];
  if (r) return r;
  return isFeCode(issue.code) ? (BY_CODE[issue.code] ?? null) : null;
}

/** 이슈 → FE 코드(서버가 코드를 안 주면 규칙에서 추정) */
export function codeOf(issue: Pick<ValidationIssue, "rule" | "code">): FeCode | null {
  if (isFeCode(issue.code) && FS02_CODES.includes(issue.code)) return issue.code;
  const s = stageOf(issue);
  return s ? CODE_OF_STAGE[s] : null;
}

/** 첫 실패(오류) — 학생 배너에 쓸 코드 하나 */
export function firstError(issues: readonly ValidationIssue[]): FeCode | null {
  for (const i of issues) {
    if (i.severity !== "error") continue;
    const c = codeOf(i);
    if (c) return c;
  }
  return issues.some((i) => i.severity === "error") ? "FE-101" : null;
}

/** 학생에게 부드러운 힌트로 보일 경고 코드(중복 제거, V-07 등 교사 전용 제외) */
export function studentHints(issues: readonly ValidationIssue[]): FeCode[] {
  const out: FeCode[] = [];
  for (const i of issues) {
    if (i.severity !== "warning" || i.rule.toUpperCase() === "V-07") continue;
    const c = codeOf(i);
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * 단계 표시. issues=null 이면 검사 중. withProgram=false(전시만)이면 프로그램 단계는 'skip'.
 */
export function stageViews(
  issues: readonly ValidationIssue[] | null,
  withProgram: boolean,
): StageView[] {
  if (!issues)
    return STAGES.map((id) => ({
      id,
      state: id === "program" && !withProgram ? "skip" : "checking",
    }));
  const failAt = (() => {
    for (const i of issues) {
      if (i.severity !== "error") continue;
      const s = stageOf(i) ?? "shape";
      return STAGES.indexOf(s);
    }
    return -1;
  })();
  const hinted = new Set(
    issues
      .filter((i) => i.severity === "warning" && i.rule.toUpperCase() !== "V-07")
      .map(stageOf)
      .filter(Boolean),
  );
  return STAGES.map((id, idx) => {
    if (id === "program" && !withProgram) return { id, state: "skip" };
    if (failAt >= 0 && idx === failAt) return { id, state: "fail" };
    if (failAt >= 0 && idx > failAt) return { id, state: "unchecked" };
    return { id, state: hinted.has(id) ? "hint" : "done" };
  });
}

// ── 목록(FS-02-01) ──────────────────────────────────────────────

/** 학생 목록에 보일 조립 — 대체된(superseded) 가져오기는 숨기고 최근 받은 순 */
export function visibleImports(campus: readonly CampusSource[]): CampusSource[] {
  return campus
    .filter((c) => c.status !== "superseded")
    .sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt));
}

/** Bench 목록 — 최근 저장 순 */
export function visibleProjects(bench: readonly BenchSource[]): BenchSource[] {
  return [...bench].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}

/**
 * 기본 짝 — 저장본(versionHash)이 있는 SPIKE 중에서 조립과 제목이 같은 것, 없으면 가장 최근 것,
 * 그런 SPIKE 가 없으면 전시만(null). 저장본 없는 프로젝트는 고를 수는 있지만 기본값이 되지 않는다(FE-201).
 */
export function defaultProject(
  imp: CampusSource | null,
  projects: readonly BenchSource[],
): string | null {
  const spike = projects.filter((p) => boardKind(p.board) === "spike" && !!p.versionHash);
  if (imp) {
    const t = imp.title.replace(/\s+/g, "");
    const same = spike.find((p) => p.title.replace(/\s+/g, "").startsWith(t));
    if (same) return same.projectId;
  }
  return spike[0]?.projectId ?? null;
}

/** 확정 전 정적 전시 여부 — 전시만 · micro:bit(SR-RUN-07). V-07 판정은 스냅샷 응답의 static 이 정본 */
export function isStaticSelection(project: BenchSource | null): boolean {
  return !project || boardKind(project.board) === "microbit";
}

/**
 * 고른 것으로 정적 전시의 이유를 고른다 — 전시만 → 프로그램 없음 · micro:bit → micro:bit ·
 * SPIKE 인데 정적(검증 응답 static) → 구동 바퀴에 모터가 닿지 않음(wheelWarning).
 */
export function staticSelectionReason(project: BenchSource | null): StaticReason {
  return staticReason(project ? project.board : null);
}
